import { Injectable } from '@angular/core';
import {
  ClinicalReport, ClinicalReportDiagnosisCategory, ClinicalReportDiagnosisType,
} from '../../domain/models/clinical-report.model';
import { ClinicalReportDiagnosticoDto, ClinicalReportDto } from '../dtos/clinical-report.dto';

@Injectable()
export class ClinicalReportMapper {
  // Every field falls back to `null`/`[]` rather than being assumed present: `responseSchema`'s
  // `optionalProperties` means Gemini can (and regularly will) omit a key it found nothing for.
  public toClinicalReport(dto: ClinicalReportDto): ClinicalReport {
    return {
      resena: dto.resena ?? null,
      motivoConsulta: dto.motivoConsulta ?? null,
      anamnesis: {
        vacunacion: dto.anamnesis?.vacunacion ?? null,
        desparasitacion: dto.anamnesis?.desparasitacion ?? null,
        dieta: dto.anamnesis?.dieta ?? null,
        entorno: dto.anamnesis?.entorno ?? null,
        tratamientoActual: dto.anamnesis?.tratamientoActual ?? null,
      },
      antecedentes: (dto.antecedentes ?? []).map((item) => ({
        descripcion: item.descripcion,
        fecha: item.fecha ?? null,
      })),
      enfermedadActual: (dto.enfermedadActual ?? []).map((item) => ({
        fecha: item.fecha ?? null,
        descripcion: item.descripcion,
      })),
      exploracionYPruebas: (dto.exploracionYPruebas ?? []).map((item) => ({
        tipo: item.tipo,
        fecha: item.fecha ?? null,
        hallazgos: item.hallazgos,
        conclusion: item.conclusion ?? null,
        citaTextual: item.citaTextual ?? null,
      })),
      diagnosticos: (dto.diagnosticos ?? []).map((item) => this.toDiagnostico(item)),
      plan: {
        tratamientos: (dto.plan?.tratamientos ?? []).map((item) => ({
          farmaco: item.farmaco,
          dosis: item.dosis ?? null,
          via: item.via ?? null,
          notas: item.notas ?? null,
          citaTextual: item.citaTextual ?? null,
        })),
        pruebasPendientes: dto.plan?.pruebasPendientes ?? [],
        manejo: dto.plan?.manejo ?? [],
      },
      pronostico: dto.pronostico ?? null,
      recomendacionesPropietario: dto.recomendacionesPropietario ?? null,
      datosNoEncontrados: dto.datosNoEncontrados ?? [],
      inconsistencias: (dto.inconsistencias ?? []).map((item) => ({
        descripcion: item.descripcion,
        fragmentos: item.fragmentos ?? [],
      })),
    };
  }

  // A value outside the two-member enum should never happen -- `responseSchema` constrains it --
  // but the fallback is what stands between a malformed response and a crash if it ever does.
  private toDiagnostico(dto: ClinicalReportDiagnosticoDto): ClinicalReport['diagnosticos'][number] {
    return {
      nombre: dto.nombre,
      categoria: this.toDiagnosisCategory(dto.categoria),
      tipo: this.toDiagnosisType(dto.tipo),
      evidencia: dto.evidencia,
      citaTextual: dto.citaTextual ?? null,
    };
  }

  private toDiagnosisCategory(value: string | undefined): ClinicalReportDiagnosisCategory {
    return value === 'principal' ? 'principal' : 'secundario';
  }

  private toDiagnosisType(value: string | undefined): ClinicalReportDiagnosisType {
    return value === 'definitivo' ? 'definitivo' : 'diferencial';
  }
}
